import {
  FeatureFlagDomain,
  FeatureFlagStability,
  FlagEntry,
} from './types';

/**
 * Single source of truth for feature flags surfaced in the admin UI.
 *
 * Adding a flag here makes it appear automatically in both:
 * - the Global App Config "Feature Flags" panel,
 * - the per-therapist override section (when `therapistField` is set).
 *
 * Two independent classifications:
 * - `domain`: where the flag's effect is visible (mobile/web/transcription/migration).
 * - `stability`: `proposed` vs `mature` (shipped / stable product surface).
 *   Mature flags have read-only global controls in admin and hide the
 *   per-therapist override switch (tagged with a "Mature" chip).
 *   Use `proposed` when the global toggle must stay editable (e.g. migration / review).
 *   `adminGlobalReadOnly` marks legacy compatibility flags (stricter tooltip).
 *
 * Keep this list aligned with `therapistAppConfigSchema` and `therapistsSchema`
 * on the backend.
 */
export const featureFlagRegistry: readonly FlagEntry[] = [
  // --- Mobile (therapist app) -----------------------------------------------
  {
    key: 'ff_modernAgentPrompts',
    kind: 'boolean',
    label: 'Modern Agent Prompts',
    description:
      'Enable the new combined OpenAI prompt structure with a single system message and per-instruction tags.',
    domain: 'mobile',
    stability: 'mature',
    globalField: 'ff_modernAgentPrompts',
    therapistField: 'ff_modernAgentPrompts',
    merge: 'or',
  },
  {
    key: 'ff_promptAddClientPronounsAfterSessionNote',
    kind: 'boolean',
    label: 'Prompt for Client Pronouns',
    description:
      'After committing a session note, prompt the therapist to add client pronouns when missing.',
    domain: 'mobile',
    stability: 'mature',
    globalField: 'ff_promptAddClientPronounsAfterSessionNote',
    therapistField: 'ff_promptAddClientPronounsAfterSessionNote',
    merge: 'or',
  },
  {
    key: 'ff_customReports',
    kind: 'boolean',
    label: 'Custom Reports (mobile)',
    description:
      'Mobile / native: therapists can create custom report prompts and pick them in session note format settings.',
    domain: 'mobile',
    stability: 'mature',
    globalField: 'ff_customReports',
    therapistField: 'ff_customReports',
    merge: 'or',
  },
  {
    key: 'ff_magicEdit',
    kind: 'boolean',
    label: 'Magic Edit (mobile)',
    description:
      'Therapists can use AI to edit session notes via instructions on the mobile app.',
    domain: 'mobile',
    stability: 'mature',
    globalField: 'ff_magicEdit',
    therapistField: 'ff_magicEdit',
    merge: 'or',
  },
  {
    key: 'ff_customPromptAudioSupportMode',
    kind: 'enum',
    label: 'Custom prompt audio support',
    description:
      'Custom-prompt audio behavior: disabled (no audio), inline (mic in editor), or exclusive (Text or Audio decision on Select Report Type).',
    domain: 'mobile',
    stability: 'mature',
    globalField: 'ff_customPromptAudioSupportMode',
    therapistField: 'ff_customPromptAudioSupportMode',
    options: ['disabled', 'inline', 'exclusive'],
    defaultOption: 'inline',
    merge: 'therapistReplacesGlobal',
  },
  {
    key: 'ff_quickFacts',
    kind: 'boolean',
    label: 'Quick Facts',
    description:
      'Permanently enabled; kept for backward compatibility. The flag will be removed in a future update.',
    domain: 'mobile',
    stability: 'mature',
    adminGlobalReadOnly: true,
    globalField: 'ff_quickFacts',
    merge: 'globalOnly',
  },
  {
    key: 'ff_calendarIntegration',
    kind: 'boolean',
    label: 'Calendar Integration',
    description:
      'Permanently enabled; kept for backward compatibility. The flag will be removed in a future update.',
    domain: 'mobile',
    stability: 'mature',
    adminGlobalReadOnly: true,
    globalField: 'ff_calendarIntegration',
    merge: 'globalOnly',
  },
  {
    key: 'stripeEnabled',
    kind: 'boolean',
    label: 'Stripe Web Subscription',
    description:
      'Permanently enabled; kept for backward compatibility. The flag will be removed in a future update.',
    domain: 'mobile',
    stability: 'mature',
    adminGlobalReadOnly: true,
    globalField: 'stripeEnabled',
    merge: 'globalOnly',
  },
  {
    key: 'notePreferences_enabled',
    kind: 'boolean',
    label: 'Note preferences',
    description:
      'Session note personalization (wording, practitioner/recipient labels, etc.). Stable; the flag will be removed in a future update.',
    domain: 'mobile',
    stability: 'mature',
    adminGlobalReadOnly: true,
    globalField: 'notePreferences',
    globalObjectBooleanKey: 'enabled',
    merge: 'globalOnly',
  },

  // --- Web (therapists-web) -------------------------------------------------
  {
    key: 'ff_web_magicEdit',
    kind: 'boolean',
    label: 'Magic Edit (web)',
    description:
      'Therapists-web only: Magic Edit on session notes. Independent from mobile Magic Edit.',
    domain: 'web',
    stability: 'mature',
    globalField: 'ff_web_magicEdit',
    therapistField: 'ff_web_magicEdit',
    merge: 'or',
  },
  {
    key: 'ff_web_reprocessing',
    kind: 'boolean',
    label: 'Reprocess note (web)',
    description:
      'Therapists-web only: reprocess session notes from the web client.',
    domain: 'web',
    stability: 'mature',
    globalField: 'ff_web_reprocessing',
    therapistField: 'ff_web_reprocessing',
    merge: 'or',
  },
  {
    key: 'ff_web_customReports',
    kind: 'boolean',
    label: 'Custom Reports (web)',
    description:
      'Therapists-web only: Custom Reports / custom prompt frameworks UI. Independent from mobile ff_customReports.',
    domain: 'web',
    stability: 'mature',
    globalField: 'ff_web_customReports',
    therapistField: 'ff_web_customReports',
    merge: 'or',
  },
  {
    key: 'ff_insuranceCompanyFilter',
    kind: 'boolean',
    label: 'Insurance Note Check (FTIC)',
    description:
      'Therapists see the Insurance Note Check button on session notes (web).',
    domain: 'web',
    stability: 'proposed',
    globalField: 'ff_insuranceCompanyFilter',
    therapistField: 'ff_insuranceCompanyFilter',
    merge: 'or',
  },

  // --- Transcription Pipeline ----------------------------------------------
  {
    key: 'ff_transcription_serverChunks',
    kind: 'boolean',
    label: 'Server-chunk transcription',
    description:
      'Recording upload uses server-side chunk transcription (batching, diarization enrollment, etc.). Does not by itself enable Temporal.',
    domain: 'transcription',
    stability: 'proposed',
    globalField: 'ff_transcription_serverChunks',
    therapistField: 'ff_transcription_serverChunks',
    merge: 'or',
  },
  {
    key: 'ff_transcription_speakerEnrollment',
    kind: 'boolean',
    label: 'Speaker enrollment pipeline',
    description:
      'Requires server-chunk transcription. Session-note–centric batching with optional speaker enrollment for diarization models.',
    domain: 'transcription',
    stability: 'proposed',
    globalField: 'ff_transcription_speakerEnrollment',
    therapistField: 'ff_transcription_speakerEnrollment',
    merge: 'or',
  },
  {
    key: 'ff_captureRecordingAnalysis',
    kind: 'boolean',
    label: 'Capture recording analysis (RMS)',
    description:
      'Therapist app persists expo-audio-studio analysis batches and attaches merged recordingAnalysis to uploads.',
    domain: 'transcription',
    stability: 'proposed',
    globalField: 'ff_captureRecordingAnalysis',
    merge: 'globalOnly',
  },
  {
    key: 'ff_temporal',
    kind: 'boolean',
    label: 'Temporal orchestration',
    description:
      'Ingest and admin paths may start Temporal workflows for transcription and asset aggregate/process pipelines. Independent from server-side chunk batching.',
    domain: 'transcription',
    stability: 'mature',
    globalField: 'ff_temporal',
    therapistField: 'ff_temporal',
    merge: 'or',
  },
  {
    key: 'ff_saveAudioToBlobStorage',
    kind: 'boolean',
    label: 'Offload audio to blob storage',
    description:
      'PowerSync ingest stores therapist recording audio in Azure Blob instead of keeping raw audio on the document.',
    domain: 'transcription',
    stability: 'mature',
    globalField: 'ff_saveAudioToBlobStorage',
    therapistField: 'ff_saveAudioToBlobStorage',
    merge: 'or',
  },

  {
    key: 'ff_onDeviceTranscription',
    kind: 'enum',
    label: 'On-device transcription (iOS 26+)',
    description:
      'off: server transcription only. shadow: the device also transcribes and the server compares (server stays the source). primary: the server skips transcription and uses the device transcript, falling back to the server on failure or timeout. Pinned per note when it is created; per-therapist value replaces the global default.',
    domain: 'transcription',
    stability: 'proposed',
    globalField: 'ff_onDeviceTranscription',
    therapistField: 'ff_onDeviceTranscription',
    options: ['off', 'shadow', 'primary'],
    defaultOption: 'off',
    merge: 'therapistReplacesGlobal',
  },
  {
    key: 'ff_onDeviceTranscription_killSwitch',
    kind: 'boolean',
    label: 'On-device transcription kill switch',
    description:
      'Forces on-device transcription off for everyone, including therapist overrides. Read live by the server: notes still waiting on a device transcript fall back to server transcription at their next check (at most the wait time).',
    domain: 'transcription',
    stability: 'proposed',
    globalField: 'ff_onDeviceTranscription_killSwitch',
    merge: 'globalOnly',
  },

  // --- Migration ------------------------------------------------------------
  {
    key: 'ff_appleSsoMigrationHack',
    kind: 'boolean',
    label: 'Apple SSO Migration Hack',
    description:
      'Enables migration of Apple SSO users to Auth0. Once turned on, the global switch locks to prevent accidental disable; disabling causes data visibility issues for migrated users.',
    domain: 'migration',
    stability: 'proposed',
    globalField: 'ff_appleSsoMigrationHack',
    merge: 'globalOnly',
    lockOnceEnabled: true,
  },
  {
    key: 'ff_promptForDetails_ifMissingForApple',
    kind: 'boolean',
    label: 'Prompt for Name/Email (Apple)',
    description:
      'Force Apple SSO users to provide name and email when missing. Disable during Apple App Store review to avoid rejection.',
    domain: 'migration',
    stability: 'proposed',
    globalField: 'ff_promptForDetails_ifMissingForApple',
    merge: 'globalOnly',
  },
] as const;

const DOMAIN_ORDER: readonly FeatureFlagDomain[] = [
  'mobile',
  'web',
  'transcription',
  'migration',
];

export const DOMAIN_LABELS: Record<FeatureFlagDomain, string> = {
  mobile: 'Mobile (therapist app)',
  web: 'Web (therapists-web)',
  transcription: 'Transcription Pipeline',
  migration: 'Migration & support',
};

export const STABILITY_LABELS: Record<FeatureFlagStability, string> = {
  proposed: 'Proposed & review toggles',
  mature: 'Mature (shipped)',
};

export const groupFlagsByDomain = (
  flags: readonly FlagEntry[],
): readonly { domain: FeatureFlagDomain; entries: FlagEntry[] }[] =>
  DOMAIN_ORDER.map(domain => ({
    domain,
    entries: flags.filter(f => f.domain === domain),
  })).filter(group => group.entries.length > 0);

export const filterByStability = (
  flags: readonly FlagEntry[],
  stability: FeatureFlagStability,
): FlagEntry[] => flags.filter(f => f.stability === stability);

export const therapistOverridableFlags = featureFlagRegistry.filter(
  flag => !!flag.therapistField,
);

export const isMature = (entry: FlagEntry): boolean =>
  entry.stability === 'mature';
